import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { getActiveProvider } from '../auth/config';
import { signOutEndsPlatformSession } from '../auth/oidc';
import { signOutEverywhere } from '../auth/signOut';
import { type ThemePreference, useTheme } from '../hooks/useTheme';
import { Icon } from '../ui';
import { LEGAL_LINKS, SUPPORT_URL } from '../utils/support';
import ExternalArrowIcon from './ExternalArrowIcon';
import { idpName } from './LoginScreen';
import styles from './SettingsMenu.module.css';

interface ThemeOption {
  value: ThemePreference;
  label: string;
  icon: React.ReactElement;
}

const SunIcon = () => (
  <svg
    aria-hidden="true"
    className={styles.optionIcon}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <circle cx="12" cy="12" r="4" />
    <line x1="12" y1="2" x2="12" y2="4" />
    <line x1="12" y1="20" x2="12" y2="22" />
    <line x1="4.22" y1="4.22" x2="5.64" y2="5.64" />
    <line x1="18.36" y1="18.36" x2="19.78" y2="19.78" />
    <line x1="2" y1="12" x2="4" y2="12" />
    <line x1="20" y1="12" x2="22" y2="12" />
    <line x1="4.22" y1="19.78" x2="5.64" y2="18.36" />
    <line x1="18.36" y1="5.64" x2="19.78" y2="4.22" />
  </svg>
);

const MoonIcon = () => (
  <svg
    aria-hidden="true"
    className={styles.optionIcon}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M21 12.79A9 9 0 1111.21 3a7 7 0 009.79 9.79z" />
  </svg>
);

const MonitorIcon = () => (
  <svg
    aria-hidden="true"
    className={styles.optionIcon}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <rect x="2" y="3" width="20" height="14" rx="2" ry="2" />
    <line x1="8" y1="21" x2="16" y2="21" />
    <line x1="12" y1="17" x2="12" y2="21" />
  </svg>
);

const CheckIcon = () => (
  <svg
    aria-hidden="true"
    className={styles.checkmark}
    viewBox="0 0 16 16"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <polyline points="2 8 6.5 12.5 14 4" />
  </svg>
);

const GearIcon = () => (
  <svg
    aria-hidden="true"
    className={styles.optionIcon}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.75"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <circle cx="12" cy="12" r="3" />
    <path d="M19.4 15a1.65 1.65 0 00.33 1.82l.06.06a2 2 0 11-2.83 2.83l-.06-.06a1.65 1.65 0 00-1.82-.33 1.65 1.65 0 00-1 1.51V21a2 2 0 11-4 0v-.09A1.65 1.65 0 009 19.4a1.65 1.65 0 00-1.82.33l-.06.06a2 2 0 11-2.83-2.83l.06.06A1.65 1.65 0 004.6 15a1.65 1.65 0 00-1.51-1H3a2 2 0 110-4h.09A1.65 1.65 0 004.6 9a1.65 1.65 0 00-.33-1.82l-.06-.06a2 2 0 112.83-2.83l.06.06a1.65 1.65 0 001.82.33H9a1.65 1.65 0 001-1.51V3a2 2 0 114 0v.09a1.65 1.65 0 001 1.51 1.65 1.65 0 001.82-.33l.06-.06a2 2 0 112.83 2.83l-.06.06a1.65 1.65 0 00-.33 1.82V9a1.65 1.65 0 001.51 1H21a2 2 0 110 4h-.09a1.65 1.65 0 00-1.51 1z" />
  </svg>
);

const LifeBuoyIcon = () => (
  <svg
    aria-hidden="true"
    className={styles.optionIcon}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.75"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <circle cx="12" cy="12" r="10" />
    <circle cx="12" cy="12" r="4" />
    <line x1="4.93" y1="4.93" x2="9.17" y2="9.17" />
    <line x1="14.83" y1="14.83" x2="19.07" y2="19.07" />
    <line x1="14.83" y1="9.17" x2="19.07" y2="4.93" />
    <line x1="4.93" y1="19.07" x2="9.17" y2="14.83" />
  </svg>
);

const DocumentIcon = () => (
  <svg
    aria-hidden="true"
    className={styles.optionIcon}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.75"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" />
    <polyline points="14 2 14 8 20 8" />
    <line x1="8" y1="13" x2="16" y2="13" />
    <line x1="8" y1="17" x2="13" y2="17" />
  </svg>
);

const ChevronRightIcon = () => (
  <svg
    aria-hidden="true"
    className={styles.submenuChevron}
    viewBox="0 0 16 16"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <polyline points="6 3 11 8 6 13" />
  </svg>
);

const SignOutIcon = () => (
  <svg
    aria-hidden="true"
    className={styles.optionIcon}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.75"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M9 21H5a2 2 0 01-2-2V5a2 2 0 012-2h4" />
    <polyline points="16 17 21 12 16 7" />
    <line x1="21" y1="12" x2="9" y2="12" />
  </svg>
);

const BackIcon = () => (
  <svg
    aria-hidden="true"
    className={styles.backIcon}
    viewBox="0 0 16 16"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <polyline points="10 3 5 8 10 13" />
  </svg>
);

const THEME_OPTIONS: ThemeOption[] = [
  { value: 'light', label: 'Light', icon: <SunIcon /> },
  { value: 'dark', label: 'Dark', icon: <MoonIcon /> },
  { value: 'system', label: 'System', icon: <MonitorIcon /> },
];

type View = 'root' | 'theme';

/** Positions of the root menu's items. `handleItemKeyDown` takes the index rather
    than reading it off the DOM, so these have to be kept in step with the JSX by
    hand. The flyout's own items are not in this list: they sit outside `menuRef`,
    which is what keeps the two arrow-key paths from seeing each other. The What's
    new item is not always there, and every item below it moves down one when it is. */
const CONTACT_INDEX = 1;
const LEARN_MORE_INDEX = 2;
const SIGN_OUT_INDEX = 3;

interface Props {
  open: boolean;
  onClose: () => void;
  triggerRef: React.RefObject<HTMLButtonElement | null>;
  /** Shows the What's new item. Absent when this person has no notes to read. */
  whatsNew?: { unread: boolean; onOpen: () => void };
}

export default function SettingsMenu({ open, onClose, triggerRef, whatsNew }: Props) {
  const shift = whatsNew ? 1 : 0;
  const { theme, setTheme } = useTheme();
  const [view, setView] = useState<View>('root');
  const menuRef = useRef<HTMLDivElement>(null);
  const firstItemRef = useRef<HTMLButtonElement>(null);
  const [pos, setPos] = useState<{ bottom: number; right: number } | null>(null);
  const [learnMoreOpen, setLearnMoreOpen] = useState(false);
  const [flyoutPos, setFlyoutPos] = useState<{ left: number; top: number } | null>(null);
  const learnMoreRef = useRef<HTMLButtonElement>(null);
  const flyoutRef = useRef<HTMLDivElement>(null);
  const firstFlyoutItemRef = useRef<HTMLButtonElement>(null);
  const provider = getActiveProvider();

  // Always reopen at the root list, never mid-drill-down from a previous visit.
  useEffect(() => {
    if (open) {
      setView('root');
      setLearnMoreOpen(false);
    }
  }, [open]);

  // The flyout is measured after it renders rather than estimated, because its
  // height depends on how many of the documents are published. Clamped so it can
  // never run off the bottom of a short window.
  useLayoutEffect(() => {
    if (!learnMoreOpen) {
      setFlyoutPos(null);
      return;
    }
    const item = learnMoreRef.current?.getBoundingClientRect();
    if (!item) return;
    const height = flyoutRef.current?.offsetHeight ?? 0;
    setFlyoutPos({
      left: item.right + 4,
      top: Math.max(8, Math.min(item.top - 8, window.innerHeight - 8 - height)),
    });
  }, [learnMoreOpen]);

  // Portaled to document.body to escape the sidebar footer's overflow:hidden.
  // Position fixed above the trigger, right-aligned; recompute on resize.
  useLayoutEffect(() => {
    if (!open) return;
    const compute = () => {
      const r = triggerRef.current?.getBoundingClientRect();
      if (r) setPos({ bottom: window.innerHeight - r.top + 8, right: window.innerWidth - r.right });
    };
    compute();
    window.addEventListener('resize', compute);
    return () => window.removeEventListener('resize', compute);
  }, [open, triggerRef]);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      const target = e.target as Node;
      if (
        menuRef.current &&
        !menuRef.current.contains(target) &&
        !flyoutRef.current?.contains(target) &&
        triggerRef.current &&
        !triggerRef.current.contains(target)
      ) {
        onClose();
      }
    };
    // Defer so the same click that opens doesn't immediately close
    const t = setTimeout(() => document.addEventListener('mousedown', handler), 0);
    return () => {
      clearTimeout(t);
      document.removeEventListener('mousedown', handler);
    };
  }, [open, onClose, triggerRef]);

  // Close on Escape
  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        // One level at a time. Handled here rather than in the flyout's own
        // keydown, because this listener is on `document` and a portaled
        // element's key event reaches it either way.
        if (learnMoreOpen) {
          setLearnMoreOpen(false);
          learnMoreRef.current?.focus();
          return;
        }
        onClose();
        triggerRef.current?.focus();
      }
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [open, onClose, triggerRef, learnMoreOpen]);

  // Focus the first item whenever the menu opens or the view changes
  // biome-ignore lint/correctness/useExhaustiveDependencies: intentional fire-on-change — body doesn't read view; it's the trigger to refocus after switching views
  useEffect(() => {
    if (open) {
      setTimeout(() => firstItemRef.current?.focus(), 0);
    }
  }, [open, view]);

  // Arrow key navigation between the currently visible view's items
  const handleItemKeyDown = (e: React.KeyboardEvent<HTMLButtonElement>, index: number) => {
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault();
      const dir = e.key === 'ArrowDown' ? 1 : -1;
      const buttons = menuRef.current?.querySelectorAll<HTMLButtonElement>(
        '[role="menuitem"], [role="menuitemradio"]',
      );
      if (!buttons?.length) return;
      const next = (index + dir + buttons.length) % buttons.length;
      buttons[next]?.focus();
    }
  };

  // The flyout has its own key path, because it is not inside `menuRef` and the
  // root handler cannot see it. Left returns to the parent item, which is the
  // half a keyboard user has no other way to do.
  const handleFlyoutKeyDown = (e: React.KeyboardEvent<HTMLButtonElement>, index: number) => {
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault();
      const dir = e.key === 'ArrowDown' ? 1 : -1;
      const buttons = flyoutRef.current?.querySelectorAll<HTMLButtonElement>('[role="menuitem"]');
      if (!buttons?.length) return;
      buttons[(index + dir + buttons.length) % buttons.length]?.focus();
    } else if (e.key === 'ArrowLeft') {
      e.preventDefault();
      setLearnMoreOpen(false);
      learnMoreRef.current?.focus();
    }
  };

  if (!open) return null;

  return createPortal(
    <>
      <div
        ref={menuRef}
        className={styles.popover}
        role="menu"
        aria-label={view === 'theme' ? 'Appearance settings' : 'Menu'}
        style={pos ? { bottom: pos.bottom, right: pos.right } : { visibility: 'hidden' }}
      >
        {view === 'root' && (
          <div className={styles.section}>
            <button
              ref={firstItemRef}
              type="button"
              role="menuitem"
              className={styles.option}
              onClick={() => {
                setLearnMoreOpen(false);
                setView('theme');
              }}
              onMouseEnter={() => setLearnMoreOpen(false)}
              onKeyDown={(e) => handleItemKeyDown(e, 0)}
            >
              <GearIcon />
              Settings
            </button>
            {whatsNew && (
              <button
                type="button"
                role="menuitem"
                className={styles.option}
                onClick={() => {
                  onClose();
                  // Focus goes to the trigger first, so the dialog hands it back
                  // there on close rather than to a menu item that no longer exists.
                  triggerRef.current?.focus();
                  whatsNew.onOpen();
                }}
                onMouseEnter={() => setLearnMoreOpen(false)}
                onKeyDown={(e) => handleItemKeyDown(e, 1)}
              >
                <Icon name="sparkle" size={15} className={styles.optionIcon} />
                What's new
                {whatsNew.unread && (
                  <span className={styles.unreadDot} role="img" aria-label="unread" />
                )}
              </button>
            )}
            <button
              type="button"
              role="menuitem"
              className={styles.option}
              onClick={() => {
                window.open(SUPPORT_URL, '_blank', 'noopener,noreferrer');
                onClose();
                triggerRef.current?.focus();
              }}
              onMouseEnter={() => setLearnMoreOpen(false)}
              onKeyDown={(e) => handleItemKeyDown(e, CONTACT_INDEX + shift)}
            >
              <LifeBuoyIcon />
              Contact Support
              <ExternalArrowIcon className={styles.externalIcon} />
            </button>
            {/* A4 of the legal checklist: a notice shown once at first run and never
              findable again does not satisfy the duty to inform, so the documents
              live here permanently. Behind one parent item rather than listed in
              the root, which keeps the top level at four entries and leaves room
              for more documents later. */}
            <button
              ref={learnMoreRef}
              type="button"
              role="menuitem"
              aria-haspopup="menu"
              aria-expanded={learnMoreOpen}
              className={`${styles.option} ${learnMoreOpen ? styles.optionOpen : ''}`}
              // Opens rather than toggles. Hovering already opened it, so a toggle
              // here closed the submenu on the click of the very item that owns it.
              // Focus moves with it, which is what makes Enter work as well as
              // ArrowRight for a keyboard user.
              onClick={() => {
                setLearnMoreOpen(true);
                setTimeout(() => firstFlyoutItemRef.current?.focus(), 0);
              }}
              onMouseEnter={() => setLearnMoreOpen(true)}
              onKeyDown={(e) => {
                if (e.key === 'ArrowRight') {
                  e.preventDefault();
                  setLearnMoreOpen(true);
                  setTimeout(() => firstFlyoutItemRef.current?.focus(), 0);
                  return;
                }
                handleItemKeyDown(e, LEARN_MORE_INDEX + shift);
              }}
            >
              <DocumentIcon />
              Learn more
              <ChevronRightIcon />
            </button>
            <button
              type="button"
              role="menuitem"
              className={styles.option}
              onClick={() => {
                onClose();
                // Ends all three sessions, not just ours: see auth/signOut.ts for why the
                // platform half has to happen in a second tab. Called synchronously from
                // the click, because a browser only allows that tab while the click is
                // still being handled. Nothing is awaited: this unmounts the menu and the
                // app below it, so there is nothing left to report a failure to, and a
                // sign-out that cannot clear our session leaves the app as it was, which
                // is the safe direction.
                signOutEverywhere();
              }}
              onMouseEnter={() => setLearnMoreOpen(false)}
              onKeyDown={(e) => handleItemKeyDown(e, SIGN_OUT_INDEX + shift)}
            >
              <SignOutIcon />
              Sign out
            </button>
            {/* The one consequence of signing out that is not obvious from the word: on MIKE
              Cloud it ends the identity provider's session in this browser too, so the next
              sign-in on a personal machine asks for credentials again. Said here, once,
              rather than discovered at the next sign-in. Absent for Entra, whose sign-out
              leaves Microsoft's session alone, and where no provider is active. */}
            {provider && signOutEndsPlatformSession(provider) && (
              <p className={styles.sectionLabel}>
                Also signs you out of {idpName(provider)} in this browser.
              </p>
            )}
          </div>
        )}

        {view === 'theme' && (
          <div className={styles.section}>
            <button
              ref={firstItemRef}
              type="button"
              role="menuitem"
              className={styles.backRow}
              onClick={() => setView('root')}
              onKeyDown={(e) => handleItemKeyDown(e, 0)}
            >
              <BackIcon />
              Settings
            </button>
            <div className={styles.sectionLabel}>Appearance</div>
            {THEME_OPTIONS.map((opt, i) => {
              const isActive = theme === opt.value;
              return (
                <button
                  key={opt.value}
                  type="button"
                  role="menuitemradio"
                  aria-checked={isActive}
                  className={`${styles.option} ${isActive ? styles.optionActive : ''}`}
                  onClick={() => {
                    setTheme(opt.value);
                    onClose();
                    triggerRef.current?.focus();
                  }}
                  onKeyDown={(e) => handleItemKeyDown(e, i + 1)}
                >
                  {opt.icon}
                  {opt.label}
                  {isActive && <CheckIcon />}
                </button>
              );
            })}
          </div>
        )}
      </div>

      {/* A sibling of the popover rather than a child, because `.popover` clips its
          overflow to keep hover backgrounds inside its rounded corners, and a
          nested flyout would be clipped along with them. */}
      {learnMoreOpen && view === 'root' && (
        <div
          ref={flyoutRef}
          className={styles.flyout}
          role="menu"
          aria-label="Learn more"
          style={
            flyoutPos ? { left: flyoutPos.left, top: flyoutPos.top } : { visibility: 'hidden' }
          }
        >
          {LEGAL_LINKS.map((link, i) => (
            <button
              key={link.key}
              ref={i === 0 ? firstFlyoutItemRef : undefined}
              type="button"
              role="menuitem"
              // `aria-disabled` rather than `disabled`: an entry whose document is not
              // published yet stays focusable, so it keeps its place in the arrow-key
              // path and is announced rather than silently skipped.
              aria-disabled={link.url ? undefined : true}
              className={`${styles.option} ${link.url ? '' : styles.optionDisabled}`}
              onClick={() => {
                if (!link.url) return;
                window.open(link.url, '_blank', 'noopener,noreferrer');
                onClose();
                triggerRef.current?.focus();
              }}
              onKeyDown={(e) => handleFlyoutKeyDown(e, i)}
            >
              <DocumentIcon />
              {link.label}
              {link.url ? <ExternalArrowIcon className={styles.externalIcon} /> : null}
            </button>
          ))}
          {LEGAL_LINKS.some((link) => !link.url) && (
            <p className={styles.note}>Published before the beta opens.</p>
          )}
        </div>
      )}
    </>,
    document.body,
  );
}
